import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Spin,
  Tabs,
  Typography,
  message,
  Space,
} from "antd";
import {
  FaFileExport,
  FaChartLine,
  FaUsers,
  FaBoxes,
  FaShoppingCart,
  FaMapMarkerAlt,
  FaFileAlt,
  FaSync,
} from "react-icons/fa";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getReports } from "../../services/ReportService";
import { asyncStatus } from "../../utils/asyncStatus";
import SalesReport from "./SaleReports";
import CustomerReport from "./CustomerReport";
import ProductReport from "./ProductReport";
import OrderReport from "./OrderReport";
import LocationReport from "./LocationReports";
import { exportReportsToCSV } from "../../utils/helper";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const ReportsDashboard = () => {
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days"),
    moment(),
  ]);
  const [activeTab, setActiveTab] = useState("sales");

  const dispatch = useDispatch();
  const { status, error, reports } = useSelector(
    (state) => state.reportsManagment
  );
  // console.log(reports);
  // In a real application, this would come from your Redux store
  // const reports = [];

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const fetchReports = () => {
    dispatch(
      getReports({
        startDate: dateRange[0].startOf("day").toISOString(),
        endDate: dateRange[1].endOf("day").toISOString(),
      })
    );
  };

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange(dates);
    }
  };

  const handleRefresh = () => {
    fetchReports();
  };

  const handleExport = () => {
    if (!reports) return;

    let exportData = [];
    let filename = "report";

    switch (activeTab) {
      case "sales":
        exportData =
          reports.graph?.salesTrend?.map((item) => ({
            Date: item._id,
            "Total Sales": item.totalSales,
            "Order Count": item.orderCount,
          })) || [];
        filename = "sales-report";
        break;

      case "customers":
        exportData =
          reports.customers?.topCustomers?.map((item) => ({
            "Customer Name": item.customerName,
            Email: item.email,
            "Total Spent": item.totalSpent,
            "Order Count": item.orderCount,
          })) || [];
        filename = "customer-report";
        break;

      case "products":
        exportData =
          reports.products?.bestSellingProducts?.map((item) => ({
            "Product Name": item.productName,
            "Quantity Sold": item.totalQuantity,
            Revenue: item.totalRevenue,
            Status: item.productStatus,
          })) || [];
        filename = "product-report";
        break;

      case "orders":
        exportData =
          reports.orders?.ordersByStatus?.map((item) => ({
            Status: item._id,
            Count: item.count,
            "Total Amount": item.totalAmount,
          })) || [];
        filename = "order-report";
        break;

      case "locations":
        exportData =
          reports.locations?.salesByLocation?.map((item) => ({
            Location: item._id,
            "Total Sales": item.totalSales,
            "Order Count": item.orderCount,
          })) || [];
        filename = "location-report";
        break;
    }

    if (exportData.length > 0) {
      exportReportsToCSV(
        exportData,
        `${filename}-${moment().format("YYYY-MM-DD")}.csv`
      );
      message.success("Export completed successfully");
    } else {
      message.warning("No data available to export");
    }
  };

  const handleExportSummary = () => {
    if (!reports) return;

    const summaryData = [
      {
        Metric: "Total Sales",
        Value: `$${reports.sales?.totalSales?.toFixed(2) || "0.00"}`,
      },
      { Metric: "Total Orders", Value: reports.sales?.totalOrders || 0 },
      {
        Metric: "Average Order Value",
        Value: `$${reports.sales?.averageOrderValue?.toFixed(2) || "0.00"}`,
      },
      { Metric: "New Customers", Value: reports.customers?.newCustomers || 0 },
      {
        Metric: "Total Customers",
        Value: reports.customers?.totalCustomers || 0,
      },
      {
        Metric: "Best Selling Product",
        Value: reports.products?.bestSellingProducts[0]?.productName || "N/A",
      },
      {
        Metric: "Top Location",
        Value: reports.locations?.salesByLocation[0]?._id || "N/A",
      },
    ];

    exportReportsToCSV(
      summaryData,
      `summary-report-${moment().format("YYYY-MM-DD")}.csv`
    );
    message.success("Summary exported successfully");
  };

  return (
    <div className="container mx-auto p-6">
      <Card className="shadow-lg rounded-lg">
        <div className="flex justify-between items-center mb-6">
          <Title level={3} className="m-0">
            <FaChartLine className="mr-2" /> Business Reports
          </Title>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={handleDateChange}
              ranges={{
                Today: [moment().startOf("day"), moment().endOf("day")],
                "This Week": [moment().startOf("week"), moment().endOf("week")],
                "This Month": [
                  moment().startOf("month"),
                  moment().endOf("month"),
                ],
                "This Year": [moment().startOf("year"), moment().endOf("year")],
              }}
            />
            <Button
              icon={<FaFileExport />}
              onClick={handleExport}
              loading={status === asyncStatus.LOADING}
            >
              Export {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </Button>
            <Button
              icon={<FaFileAlt />}
              onClick={handleExportSummary}
              loading={status === asyncStatus.LOADING}
            >
              Export Summary
            </Button>
            <Button
              icon={<FaSync />}
              onClick={handleRefresh}
              loading={status === asyncStatus.LOADING}
            >
              Refresh
            </Button>
          </Space>
        </div>

        {status === asyncStatus.LOADING ? (
          <div className="flex justify-center my-8">
            <Spin size="large" />
          </div>
        ) : error ? (
          <div className="text-red-500 text-center my-8">
            Failed to load reports: {error.message}
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <Row gutter={16} className="mb-6">
              <Col span={6}>
                <Card>
                  <Text strong>Total Sales</Text>
                  <Title level={3}>
                    ${reports?.sales?.totalSales?.toFixed(2) || "0.00"}
                  </Title>
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Text strong>Total Orders</Text>
                  <Title level={3}>{reports?.sales?.totalOrders || 0}</Title>
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Text strong>New Customers</Text>
                  <Title level={3}>
                    {reports?.customers?.newCustomers || 0}
                  </Title>
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Text strong>Avg. Order Value</Text>
                  <Title level={3}>
                    ${reports?.sales?.averageOrderValue?.toFixed(2) || "0.00"}
                  </Title>
                </Card>
              </Col>
            </Row>

            {/* Report Tabs */}
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane
                key="sales"
                tab={
                  <span>
                    <FaChartLine className="mr-1" /> Sales
                  </span>
                }
              >
                <SalesReport
                  salesData={reports?.sales}
                  trendData={reports?.graph?.salesTrend}
                />
              </TabPane>
              <TabPane
                key="customers"
                tab={
                  <span>
                    <FaUsers className="mr-1" /> Customers
                  </span>
                }
              >
                <CustomerReport
                  customerData={reports?.customers}
                  growthData={reports?.graph?.customerGrowth}
                />
              </TabPane>
              <TabPane
                key="products"
                tab={
                  <span>
                    <FaBoxes className="mr-1" /> Products
                  </span>
                }
              >
                <ProductReport
                  productData={reports?.products}
                  performanceData={reports?.graph?.productPerformance}
                />
              </TabPane>
              <TabPane
                key="orders"
                tab={
                  <span>
                    <FaShoppingCart className="mr-1" /> Orders
                  </span>
                }
              >
                <OrderReport orderData={reports?.orders} />
              </TabPane>
              <TabPane
                key="locations"
                tab={
                  <span>
                    <FaMapMarkerAlt className="mr-1" /> Locations
                  </span>
                }
              >
                <LocationReport locationData={reports?.locations} />
              </TabPane>
            </Tabs>
          </>
        )}
      </Card>
    </div>
  );
};

export default ReportsDashboard;
